import { useCallback, useEffect, useState } from 'react';
import { Edit2, SeparatorHorizontal } from '@/components/icons';
import {
    Command,
    CommandEmpty,
    CommandGroup,
    CommandInput,
    CommandItem,
    CommandList,
    CommandSeparator,
} from '@/components/ui/command';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { DIVIDER_CATEGORY_GROUPS } from '@/constants/divider-categories';
import { cn } from '@/lib/utils';
import type { ReactNode } from 'react';

export interface DividerCategorySelection {
    title: string;
    description: string;
}

interface DividerCategoryPickerProps {
    onSelect: (selection: DividerCategorySelection | null) => void;
    children: ReactNode;
    disabled?: boolean;
    onOpenChange?: (open: boolean) => void;
}

export default function DividerCategoryPicker({
    onSelect,
    children,
    disabled = false,
    onOpenChange,
}: DividerCategoryPickerProps) {
    const [open, setOpen] = useState(false);

    const handleOpenChange = useCallback(
        (nextOpen: boolean) => {
            setOpen(nextOpen);
            onOpenChange?.(nextOpen);
        },
        [onOpenChange],
    );

    const handleSelectCategory = (title: string, description: string) => {
        onSelect({ title, description });
        handleOpenChange(false);
    };

    const handleSelectCustom = () => {
        onSelect(null);
        handleOpenChange(false);
    };

    useEffect(() => {
        return () => {
            if (open) {
                onOpenChange?.(false);
            }
        };
    }, [open, onOpenChange]);

    return (
        <Popover open={open} onOpenChange={handleOpenChange}>
            <PopoverTrigger asChild disabled={disabled}>
                {children}
            </PopoverTrigger>
            {/* Anchored on the placeholder inside the page, so it opens
                downwards; Radix flips it near the bottom of the paper. */}
            <PopoverContent className="w-[380px] p-0" side="bottom" align="start" sideOffset={8}>
                <Command
                    className={cn(
                        '[&_[cmdk-group-heading]]:px-3',
                        '[&_[cmdk-group-heading]]:py-2',
                        '[&_[cmdk-group-heading]]:text-[11px]',
                        '[&_[cmdk-group-heading]]:font-semibold',
                        '[&_[cmdk-group-heading]]:uppercase',
                        '[&_[cmdk-group-heading]]:tracking-wider',
                        '[&_[cmdk-group-heading]]:text-muted-foreground',
                    )}
                >
                    <CommandInput placeholder="Buscar categoria..." />
                    <CommandList className="max-h-[360px]">
                        <CommandEmpty className="py-8 text-center">
                            <p className="text-sm text-muted-foreground">
                                Nenhuma categoria encontrada.
                            </p>
                            <button
                                type="button"
                                onClick={handleSelectCustom}
                                className="mt-2 text-xs text-blue-600 underline underline-offset-2 hover:text-blue-700"
                            >
                                Adicionar divisor personalizado
                            </button>
                        </CommandEmpty>

                        {/* Custom divider — always first */}
                        <CommandGroup>
                            <CommandItem
                                value="__personalizado__"
                                onSelect={handleSelectCustom}
                                className="cursor-pointer px-3 py-2.5"
                            >
                                <div className="flex w-full items-center gap-3">
                                    <div className="flex size-8 shrink-0 items-center justify-center rounded-lg border-2 border-dashed border-border-strong">
                                        <Edit2 className="size-3.5 text-muted-foreground" />
                                    </div>
                                    <div className="min-w-0">
                                        <p className="text-sm font-medium text-foreground">
                                            Personalizado
                                        </p>
                                        <p className="truncate text-xs text-muted-foreground">
                                            Título livre, sem descrição automática
                                        </p>
                                    </div>
                                </div>
                            </CommandItem>
                        </CommandGroup>

                        <CommandSeparator />

                        {/* Category groups */}
                        {DIVIDER_CATEGORY_GROUPS.map((group) => (
                            <CommandGroup key={group.label} heading={group.label}>
                                {group.categories.map((category) => (
                                    <CommandItem
                                        key={category.id}
                                        value={`${category.term} ${category.definition}`}
                                        onSelect={() =>
                                            handleSelectCategory(category.term, category.definition)
                                        }
                                        className="cursor-pointer px-3 py-2.5"
                                    >
                                        <div className="flex w-full items-start gap-3">
                                            <div className="mt-0.5 flex size-8 shrink-0 items-center justify-center rounded-lg border border-border bg-surface">
                                                <SeparatorHorizontal className="size-3.5 text-muted-foreground" />
                                            </div>
                                            <div className="min-w-0 flex-1">
                                                <p className="text-sm font-medium leading-tight text-foreground">
                                                    {category.term}
                                                </p>
                                                <p className="mt-0.5 line-clamp-2 text-xs leading-relaxed text-muted-foreground">
                                                    {category.definition}
                                                </p>
                                            </div>
                                        </div>
                                    </CommandItem>
                                ))}
                            </CommandGroup>
                        ))}
                    </CommandList>
                </Command>
            </PopoverContent>
        </Popover>
    );
}
